import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RedeemVoucher } from "../../components/gift-cards/redeem-voucher";
import { useAppLoading } from "../../components/layout/app-loading-provider";
import { PortalMainLayout } from "../../components/layout/portal-main-layout";
import {
  CognitoContext,
  UserProviderActions,
} from "../../contexts/cognito/cognito-provider";
import { mapGroupCompanyOptions } from "../../library/core/current-company";
import { AppRouteNames } from "../../routes/app-routes";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { AuthorisedPage } from "../auth/authorised-page";
import { useToast } from "../../components/toast/toast-context";

export const RedeemVoucherPage = () => {
  const { state, dispatch } = useContext(CognitoContext);
  const [unauthorised, setUnauthorised] = useState({ show: false });
  const [companyOptions, setCompanyOptions] = useState(null);
  const { hideLoading } = useAppLoading();
  const { error: showErrorToast } = useToast();
  const [pageState, setPageState] = useState({
    isLoading: true,
    key: 1,
  });
  const navigate = useNavigate();

  const navigateToLogin = useCallback(() => {
    dispatch({
      type: UserProviderActions.setSession,
      session: null,
    });
    navigate(AppRouteNames.signin);
  }, [dispatch, navigate]);

  const getUserInfo = useCallback(
    async (forceRefresh, refreshPage) => {
      if (!state.session) return;

      const userInfoService = CoreServiceSelector.GetUserInfoService(
        state.session
      );
      if (
        forceRefresh ||
        !userInfoService.getSavedUserInfo() ||
        !state.userInfo
      ) {
        const userInfo = await userInfoService
          .getUserInfo(forceRefresh)
          .catch((err) => {
            if (err?.response?.status === 401) {
              navigateToLogin();
              return;
            }

            showErrorToast(
              err?.code === "ECONNABORTED"
                ? "The server is taking longer than expected to respond. Please try again."
                : "We couldn't load your account information. Please try again."
            );
          });

        dispatch({ type: UserProviderActions.setUserInfo, userInfo });

        if (!userInfo) return;

        const gOptions = {
          options: mapGroupCompanyOptions(userInfo.GroupCompanies),
          defaultCompanyId: userInfo.CompanyId,
          readOnly:
            !userInfo.HasFullGroupAccess || userInfo.GroupCompanies.length < 2,
        };

        setCompanyOptions(gOptions);
      }

      setPageState((prevState) => ({
        ...prevState,
        isLoading: false,
        key: refreshPage ? 1 : prevState.key + 1,
      }));
    },
    [state.session, state.userInfo, dispatch, navigateToLogin, showErrorToast]
  );

  useEffect(() => {
    if (state && state.session) {
      getUserInfo(false, true);
    }
  }, [getUserInfo, hideLoading, state, state.session]);

  useEffect(() => {
    if (!(state && state.userInfo)) return;

    setPageState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));

    if (state.userInfo.UserRoles.findIndex((role) => role === "redeem") < 0) {
      setUnauthorised({ show: true });
    }

    if (!companyOptions || companyOptions.options.length === 0) {
      const gOptions = {
        options: mapGroupCompanyOptions(state.userInfo.GroupCompanies),
        defaultCompanyId: state.userInfo.CompanyId,
        readOnly:
          !state.userInfo.HasFullGroupAccess ||
          state.userInfo.GroupCompanies.length < 2,
      };
      setCompanyOptions(gOptions);
    }

    hideLoading();
  }, [state.userInfo, companyOptions, state, hideLoading]);

  const onTrxCompletedHandler = async () => { };

  const onNewTransactionHandler = async () => {
    await getUserInfo(true, false);
  };

  const onUnauthorisedHandler = async () => {
    setUnauthorised({ show: true });
    await getUserInfo(true, false);
  };

  return (
    <AuthorisedPage>
      <PortalMainLayout isUnauthorised={unauthorised.show}>
        {companyOptions && (
          <RedeemVoucher
            key={pageState.key}
            companyOptions={companyOptions}
            onNewTransaction={onNewTransactionHandler}
            onTrxCompleted={onTrxCompletedHandler}
            onUnauthorised={onUnauthorisedHandler}
          />
        )}
      </PortalMainLayout>
    </AuthorisedPage>
  );
};
